<template>
  <section class="investment-section" id="investment">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">{{ $t('investment.sectionTitle') }}</h2>
        <p class="section-subtitle">{{ $t('investment.sectionSubtitle') }}</p>
      </div>
      
      <div class="strategy-container">
        <div class="strategy-card" v-for="(item, index) in strategies" :key="index">
          <div class="card-header">
            <div class="icon-box">
              <component :is="item.iconComponent" />
            </div>
            <h3 class="card-title">{{ item.title }}</h3>
          </div>
          
          <p class="card-description">{{ item.description }}</p>
          
          <div class="section-block">
            <h4 class="block-title">{{ item.pointsLabel }}</h4>
            <ul class="block-list">
              <li v-for="(point, i) in item.points" :key="i">
                <span class="list-dot">→</span>{{ point }}
              </li>
            </ul>
          </div>
          
          <div class="section-block">
            <h4 class="block-title">{{ item.directionsLabel }}</h4>
            <ul class="block-list">
              <li v-for="(dir, i) in item.directions" :key="i">
                <span class="list-dot">→</span>{{ dir }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      <div class="investment-cta">
        <h3 class="cta-title">{{ $t('investment.ctaTitle') }}</h3>
        <p class="cta-description">{{ $t('investment.ctaDesc') }}</p>
        <div class="cta-actions">
          <button class="primary-cta-button">{{ $t('investment.btnPrimary') }}</button>
          <button class="secondary-cta-button">{{ $t('investment.btnSecondary') }}</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import IconSkillith from './IconSkillith.vue'
import IconEcosystem from './IconEcosystem.vue'
import IconBiotech from './IconBiotech.vue'
import IconSecurity from './IconSecurity.vue'
import IconEducation from './IconEducation.vue'
import IconFuture from './IconFuture.vue'

export default {
  name: 'InvestmentSection',
  components: {
    IconSkillith,
    IconEcosystem,
    IconBiotech,
    IconSecurity,
    IconEducation,
    IconFuture
  },
  computed: {
    strategies() {
      const t = this.$t
      return [
        {
          iconComponent: 'IconSkillith',
          title: t('investment.strategy1Title'),
          description: t('investment.strategy1Desc'),
          pointsLabel: t('investment.pointsLabel'),
          points: [t('investment.strategy1Point1'), t('investment.strategy1Point2')],
          directionsLabel: t('investment.directionsLabel'),
          directions: [t('investment.strategy1Dir')]
        },
        {
          iconComponent: 'IconEcosystem',
          title: t('investment.strategy2Title'),
          description: t('investment.strategy2Desc'),
          pointsLabel: t('investment.pointsLabel'),
          points: [t('investment.strategy2Point1'), t('investment.strategy2Point2')],
          directionsLabel: t('investment.directionsLabel'),
          directions: [t('investment.strategy2Dir')]
        },
        {
          iconComponent: 'IconBiotech',
          title: t('investment.strategy3Title'),
          description: t('investment.strategy3Desc'),
          pointsLabel: t('investment.pointsLabel'),
          points: [t('investment.strategy3Point1'), t('investment.strategy3Point2')],
          directionsLabel: t('investment.directionsLabel'),
          directions: [t('investment.strategy3Dir')]
        },
        {
          iconComponent: 'IconSecurity',
          title: t('investment.strategy4Title'),
          description: t('investment.strategy4Desc'),
          pointsLabel: t('investment.pointsLabel'),
          points: [t('investment.strategy4Point1'), t('investment.strategy4Point2')],
          directionsLabel: t('investment.directionsLabel'),
          directions: [t('investment.strategy4Dir')]
        },
        {
          iconComponent: 'IconEducation',
          title: t('investment.strategy5Title'),
          description: t('investment.strategy5Desc'),
          pointsLabel: t('investment.pointsLabel'),
          points: [t('investment.strategy5Point1'), t('investment.strategy5Point2')],
          directionsLabel: t('investment.directionsLabel'),
          directions: [t('investment.strategy5Dir')]
        },
        {
          iconComponent: 'IconFuture',
          title: t('investment.strategy6Title'),
          description: t('investment.strategy6Desc'),
          pointsLabel: t('investment.pointsLabel'),
          points: [t('investment.strategy6Point1'), t('investment.strategy6Point2')],
          directionsLabel: t('investment.directionsLabel'),
          directions: [t('investment.strategy6Dir')]
        }
      ]
    }
  }
}
</script>

<style scoped>
.investment-section {
  padding: 6rem 0;
  background: #070B14;
  position: relative;
  overflow: hidden;
}

/* Grid background texture */
.investment-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0.3;
  pointer-events: none;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #F9FAFB;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.1rem;
  color: rgba(209, 213, 219, 0.8);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.strategy-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-bottom: 4rem;
}

.strategy-card {
  background: #0B1220;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 2rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.strategy-card:hover {
  transform: translateY(-4px);
  border-color: rgba(255, 255, 255, 0.12);
  background: #0F172A;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.card-header {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  align-items: flex-start;
}

.icon-box {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(0, 185, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.strategy-card:hover .icon-box {
  background: rgba(0, 185, 255, 0.12);
}

.icon-box :deep svg {
  width: 20px;
  height: 20px;
}

.card-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #F9FAFB;
  margin: 0;
}

.card-description {
  font-size: 0.95rem;
  color: rgba(209, 213, 219, 0.8);
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.section-block {
  margin-bottom: 1.5rem;
}

.section-block:last-child {
  margin-bottom: 0;
}

.block-title {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 0.8rem;
}

.block-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.block-list li {
  font-size: 0.9rem;
  color: rgba(209, 213, 219, 0.7);
  padding: 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.list-dot {
  color: rgba(0, 185, 255, 0.6);
  font-weight: 600;
  flex-shrink: 0;
}

.investment-cta {
  text-align: center;
  padding-top: 3rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.cta-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #F9FAFB;
  margin-bottom: 1rem;
}

.cta-description {
  font-size: 1rem;
  color: rgba(209, 213, 219, 0.8);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

.cta-actions {
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
}

.primary-cta-button {
  background: linear-gradient(135deg, #00B9FF, #00E5CC);
  color: #070B14;
  border: none;
  padding: 12px 28px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.primary-cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 185, 255, 0.3);
}

.secondary-cta-button {
  background: transparent;
  color: #F9FAFB;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 11px 27px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.secondary-cta-button:hover {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.05);
  transform: translateY(-2px);
}

@media (max-width: 1024px) {
  .strategy-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .investment-section {
    padding: 4rem 0;
  }

  .section-title {
    font-size: 2rem;
  }

  .section-subtitle {
    font-size: 1rem;
  }

  .strategy-container {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .strategy-card {
    padding: 1.5rem;
  }

  .cta-title {
    font-size: 1.5rem;
  }

  .cta-actions {
    flex-direction: column;
    align-items: center;
  }

  .primary-cta-button,
  .secondary-cta-button {
    width: 100%;
  }

  .container {
    padding: 0 1rem;
  }
}
</style>
